import React, { memo, useCallback } from 'react';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { theme } from '@garnish/constants';
import { Button, HStack, useResponsive } from '@garnish/core';
import { Modal, ModalDialogue } from '@garnish/overlays';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultCardDistanceModal = memo((props: Props) => {
  const { isVisible, location, onRequestClose, onContinue } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnContinue = useCallback(() => {
    onContinue();
    onRequestClose();
  }, [onContinue, onRequestClose]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const footerStyles =
    Platform.OS === 'android' ? match([styles.footerAndroidXS, undefined]) : undefined;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      size="medium"
      accessibilityLabel={formatMessage(messages.a11yLabel)}
      headline={formatMessage(messages.headline, { location })}
      visible={isVisible}
      onRequestClose={onRequestClose}
      withCloseBtn={false}
    >
      <SafeAreaView edges={match([['bottom'], undefined])}>
        <Modal.Footer withoutTopBorder style={footerStyles}>
          <HStack itemsPerRow={2}>
            <Button palette="secondary" size="large" onPress={onRequestClose}>
              <FormattedMessage {...messages.goBack} />
            </Button>

            <Button palette="primary" size="large" onPress={handleOnContinue}>
              <FormattedMessage {...messages.continue} />
            </Button>
          </HStack>
        </Modal.Footer>
      </SafeAreaView>
    </ModalDialogue>
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly a11yLabel: NoMessageValues;
  readonly headline: { readonly location: MessageValue };
  readonly goBack: NoMessageValues;
  readonly continue: NoMessageValues;
}>({
  a11yLabel: {
    defaultMessage: '"Far away location" modal',
    description: 'Location Search | Result Card | Distance Modal | A11y Label',
  },
  headline: {
    defaultMessage: 'Heads up - it appears you are far away from {location}',
    description: 'Location Search | Result Card | Distance Modal | Headline',
  },
  goBack: {
    defaultMessage: 'Go Back',
    description: 'Location Search | Result Card | Distance Modal | Go Back',
  },
  continue: {
    defaultMessage: 'Continue',
    description: 'Location Search | Result Card | Distance Modal | Continue',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footerAndroidXS: {
    paddingBottom: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  location: string;
  onRequestClose: () => void;
  onContinue: () => void;
};
